// KnowledgeDetailModal — opened from a citation chip under an assistant
// message ("📎 Based on 2 learned facts → view"). Shows the full lesson
// text. Admins additionally see a Delete button, gated by a
// window.confirm() per the tracker's "are you sure" requirement — this is
// the SAME DELETE /api/knowledge/{id} endpoint the standalone
// KnowledgeAdmin.jsx table uses (one mutation path, two entry points).

import React, { useState, useEffect } from "react";
import { Modal, Box, Button, SpaceBetween, Spinner, Alert, StatusIndicator } from "@cloudscape-design/components";
import { aiChatApi } from "./aiChatApi.js";

const CATEGORY_LABELS = {
  metric_definition: "Metric definition",
  correction: "Correction",
  gotcha: "Gotcha",
  preference: "Preference",
};

export default function KnowledgeDetailModal({ knowledgeId, isAdmin, onDismiss, onDeleted }) {
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    aiChatApi
      .getKnowledge(knowledgeId)
      .then((data) => { if (!cancelled) setLesson(data); })
      .catch((err) => { if (!cancelled) setError(err.message || "Failed to load."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [knowledgeId]);

  const handleDelete = async () => {
    const confirmed = window.confirm(
      "Delete this learned fact? Future answers will no longer use it."
    );
    if (!confirmed) return;
    setDeleting(true);
    try {
      await aiChatApi.deleteKnowledge(knowledgeId);
      onDeleted?.();
    } catch (err) {
      setError(err.message || "Failed to delete.");
      setDeleting(false);
    }
  };

  return (
    <Modal
      visible
      onDismiss={onDismiss}
      header="Learned fact"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            {isAdmin && lesson && (
              <Button loading={deleting} onClick={handleDelete}>Delete</Button>
            )}
            <Button variant="primary" onClick={onDismiss}>Close</Button>
          </SpaceBetween>
        </Box>
      }
    >
      {loading && <Box textAlign="center" padding="l"><Spinner size="large" /></Box>}
      {error && <Alert type="error">{error}</Alert>}
      {lesson && (
        <SpaceBetween size="s">
          <StatusIndicator type="success">
            {CATEGORY_LABELS[lesson.category] || lesson.category}
          </StatusIndicator>
          <Box variant="p">{lesson.content}</Box>
          {lesson.applies_to?.length > 0 && (
            <Box color="text-body-secondary" fontSize="body-s">
              Tags: {lesson.applies_to.join(", ")}
            </Box>
          )}
          <Box color="text-body-secondary" fontSize="body-s">
            Approved {lesson.decided_at ? new Date(lesson.decided_at).toLocaleString() : "-"}
            {lesson.decided_by ? ` by ${lesson.decided_by}` : ""}
          </Box>
        </SpaceBetween>
      )}
    </Modal>
  );
}
